import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { AlertCircle, CheckCircle, Loader2, Star, Sparkles } from "lucide-react";
import { Alert, AlertDescription } from "./components/ui/alert";

// For Netlify deployment, functions are available via /api redirect
const API = '/api';

// Helper functions for address labels
const getStateLabel = (country) => {
  switch (country) {
    case 'US': return 'State';
    case 'CA': return 'Province';
    case 'GB': return 'County';
    default: return 'State/Province';
  }
};

const getPostalLabel = (country) => {
  switch (country) {
    case 'US': return 'ZIP Code';
    case 'GB': return 'Postcode';
    case 'CA': return 'Postal Code';
    default: return 'Postal Code';
  }
};

function App() {
  // Check if we should show form-only version
  const urlParams = new URLSearchParams(window.location.search);
  const formOnly = urlParams.get('form-only') === 'true';

  const [formData, setFormData] = useState({
    customer: {
      email: "",
      name: "",
      phone: ""
    },
    shipping_address: {
      name: "",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state_province: "",
      postal_code: "",
      country: "US"
    },
    item: {
      product_id: "oracle-deck-001",
      product_name: "Mystical Oracle Card Deck",
      price: 44.00,
      quantity: 1
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [currentStep, setCurrentStep] = useState(1);
  const [orderStatus, setOrderStatus] = useState(null);
  const [preorderStats, setPreorderStats] = useState({
    current_orders: 45,
    max_orders: 100,
    percentage: 45.0,
    remaining: 55
  });

  // Check URL parameters for payment status and fetch stats
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    const cancelled = urlParams.get('cancelled');

    if (sessionId && success) {
      checkPaymentStatus(sessionId);
    } else if (cancelled) {
      setMessage({ type: "error", text: "Payment was cancelled. You can try again anytime." });
    }

    // Fetch preorder statistics
    fetchPreorderStats();
  }, []);

  const fetchPreorderStats = async () => {
    try {
      const response = await axios.get(`${API}/preorder-stats`);
      setPreorderStats(response.data);
    } catch (error) {
      console.log("Using default stats");
      // Default stats are already set in state
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      setMessage({ type: "info", text: "Checking payment status..." });
      const response = await axios.get(`${API}/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setMessage({ 
          type: "success", 
          text: "Payment successful! Your oracle card deck has been pre-ordered. Check your email for confirmation!" 
        });
        setOrderStatus("confirmed");
      } else {
        setMessage({ type: "info", text: "Payment is being processed..." });
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      setMessage({ type: "error", text: "Error checking payment status. Please contact support." });
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.customer.email && formData.customer.name;
      case 2:
        return formData.shipping_address.name && 
               formData.shipping_address.address_line_1 && 
               formData.shipping_address.city && 
               formData.shipping_address.state_province && 
               formData.shipping_address.postal_code && 
               formData.shipping_address.country;
      case 3:
        return validateStep(1) && validateStep(2); // Ensure all previous steps are valid
      default:
        return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      const requestData = {
        ...formData,
        item: {
          ...formData.item,
          price: 44.00
        },
        host_url: window.location.origin
      };

      const response = await axios.post(`${API}/preorder`, requestData);
      
      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setMessage({ 
          type: response.data.success ? "success" : "error", 
          text: response.data.message 
        });
      }
    } catch (error) {
      setMessage({ 
        type: "error", 
        text: error.response?.data?.detail || "Something went wrong. Please try again." 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentStep(currentStep - 1);
  };

  if (orderStatus === "confirmed") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-mystical-dark via-mystical-brown to-mystical-darker">
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <CheckCircle className="w-20 h-20 text-mystical-gold mx-auto mb-4" />
              <h1 className="text-4xl font-bold text-mystical-gold mb-4 title-font">
                Thank You for Your Pre-Order!
              </h1>
              <p className="text-mystical-light text-lg body-font">
                Your mystical oracle card deck will be shipped by the Christmas holidays. 
                Check your email for confirmation details.
              </p>
            </div>
            
            <Card className="bg-mystical-card border-mystical-gold/20">
              <CardContent className="p-8">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Sparkles className="w-6 h-6 text-mystical-gold" />
                  <span className="text-mystical-gold font-semibold heading-font">Your Journey Begins</span>
                  <Sparkles className="w-6 h-6 text-mystical-gold" />
                </div>
                <p className="text-mystical-light body-font">
                  Prepare to unlock the mysteries of the universe with your new oracle cards. 
                  We'll notify you as soon as they ship!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Form-only version for iframe embedding
  if (formOnly) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-mystical-dark via-mystical-brown to-mystical-darker">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-mystical-gold mb-4 title-font">
                🔮 Secure Your Oracle Deck
              </h1>
              <p className="text-mystical-light body-font">
                Complete your mystical journey in just 3 simple steps
              </p>
            </div>

            {/* Step Indicator */}
            <div className="flex justify-center mb-8 space-x-4">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step < currentStep
                      ? "bg-green-500 text-white"
                      : step === currentStep
                      ? "bg-mystical-gold text-mystical-dark"
                      : "bg-mystical-card text-mystical-muted border border-mystical-gold/30"
                  }`}
                >
                  {step < currentStep ? "✓" : step}
                </div>
              ))}
            </div>

            {/* Alert Messages */}
            {message.text && (
              <Alert className={`mb-6 ${message.type === "error" ? "border-red-500 bg-red-50" : "border-green-500 bg-green-50"}`}>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className={message.type === "error" ? "text-red-700" : "text-green-700"}>
                  {message.text}
                </AlertDescription>
              </Alert>
            )}

            {/* Multi-Step Form */}
            <Card className="bg-mystical-card border-mystical-gold/20">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit}>
                {/* Step 1: Customer Information */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-mystical-gold title-font">
                        📧 Contact Information
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="email" className="text-mystical-gold font-semibold">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.customer.email}
                          onChange={(e) => handleInputChange("customer", "email", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          placeholder="your@email.com"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="name" className="text-mystical-gold font-semibold">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.customer.name}
                          onChange={(e) => handleInputChange("customer", "name", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          placeholder="Your full name"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone" className="text-mystical-gold font-semibold">Phone Number (Optional)</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.customer.phone}
                          onChange={(e) => handleInputChange("customer", "phone", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Shipping Information */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-mystical-gold title-font">
                        🚚 Shipping Address
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="shipping-name" className="text-mystical-gold font-semibold">Recipient Name *</Label>
                        <Input
                          id="shipping-name"
                          value={formData.shipping_address.name}
                          onChange={(e) => handleInputChange("shipping_address", "name", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          placeholder="Full name for delivery"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="address1" className="text-mystical-gold font-semibold">Address Line 1 *</Label>
                        <Input
                          id="address1"
                          value={formData.shipping_address.address_line_1}
                          onChange={(e) => handleInputChange("shipping_address", "address_line_1", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          placeholder="Street address, P.O. Box"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="address2" className="text-mystical-gold font-semibold">Address Line 2 (Optional)</Label>
                        <Input
                          id="address2"
                          value={formData.shipping_address.address_line_2}
                          onChange={(e) => handleInputChange("shipping_address", "address_line_2", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          placeholder="Apartment, suite, unit, building"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="city" className="text-mystical-gold font-semibold">City *</Label>
                          <Input
                            id="city"
                            value={formData.shipping_address.city}
                            onChange={(e) => handleInputChange("shipping_address", "city", e.target.value)}
                            className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                            placeholder="City"
                            required
                          />
                        </div>

                        <div>
                          <Label htmlFor="country" className="text-mystical-gold font-semibold">Country *</Label>
                          <Select value={formData.shipping_address.country} onValueChange={(value) => handleInputChange("shipping_address", "country", value)}>
                            <SelectTrigger className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold">
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent className="bg-mystical-darker border-mystical-gold/30">
                              <SelectItem value="US">United States</SelectItem>
                              <SelectItem value="CA">Canada</SelectItem>
                              <SelectItem value="GB">United Kingdom</SelectItem>
                              <SelectItem value="AU">Australia</SelectItem>
                              <SelectItem value="DE">Germany</SelectItem>
                              <SelectItem value="FR">France</SelectItem>
                              <SelectItem value="IT">Italy</SelectItem>
                              <SelectItem value="ES">Spain</SelectItem>
                              <SelectItem value="NL">Netherlands</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="state" className="text-mystical-gold font-semibold">
                            {getStateLabel(formData.shipping_address.country)} *
                          </Label>
                          <Input
                            id="state"
                            value={formData.shipping_address.state_province}
                            onChange={(e) => handleInputChange("shipping_address", "state_province", e.target.value)}
                            className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                            placeholder={getStateLabel(formData.shipping_address.country)}
                            required
                          />
                        </div>

                        <div>
                          <Label htmlFor="postal" className="text-mystical-gold font-semibold">
                            {getPostalLabel(formData.shipping_address.country)} *
                          </Label>
                          <Input
                            id="postal"
                            value={formData.shipping_address.postal_code}
                            onChange={(e) => handleInputChange("shipping_address", "postal_code", e.target.value)}
                            className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                            placeholder={getPostalLabel(formData.shipping_address.country)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Review & Payment */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-mystical-gold title-font">
                        💳 Review & Payment
                      </h2>
                    </div>

                    {/* Price Display */}
                    <div className="text-center p-6 bg-mystical-darker rounded-lg border border-mystical-gold/30">
                      <div className="text-3xl font-bold text-mystical-gold mb-2">
                        $44.00
                      </div>
                      <p className="text-mystical-light">+ shipping calculated at checkout</p>
                    </div>

                    {/* Order Summary */}
                    <div className="bg-mystical-darker p-6 rounded-lg border border-mystical-gold/30">
                      <h3 className="text-xl font-bold text-mystical-gold mb-4">📦 Order Summary</h3>
                      <div className="space-y-2 text-mystical-light">
                        <div><strong>Product:</strong> Mystical Oracle Card Deck</div>
                        <div><strong>Quantity:</strong> 1</div>
                        <div><strong>Expected Delivery:</strong> Christmas Holiday Season</div>
                        <div><strong>Email:</strong> {formData.customer.email}</div>
                        <div><strong>Shipping to:</strong> {formData.shipping_address.city}, {formData.shipping_address.state_province}, {formData.shipping_address.country}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Navigation */}
                <div className="flex justify-between mt-8">
                  {currentStep > 1 && (
                    <Button type="button" onClick={prevStep} variant="outline" className="border-mystical-gold/30 text-mystical-gold hover:bg-mystical-gold/10">
                      ← Previous
                    </Button>
                  )}
                  
                  <div className="flex-1"></div>

                  {currentStep < 3 ? (
                    <Button type="button" onClick={nextStep} className="bg-mystical-gold text-mystical-dark hover:bg-mystical-gold/90">
                      Next Step →
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isSubmitting || !validateStep(3)}
                      className="bg-mystical-gold text-mystical-dark hover:bg-mystical-gold/90"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "🔒 Complete Order"
                      )}
                    </Button>
                  )}
                </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-mystical-dark via-mystical-brown to-mystical-darker">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-mystical-dark/90 to-mystical-brown/80 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_preorder-manager/artifacts/86vz5gcn_oracle-powerful.png')"
          }}
        ></div>
        
        <div className="relative z-20 container mx-auto px-4 py-20 text-center">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-mystical-gold/20 text-mystical-gold border-mystical-gold/30 mb-6 misc-font">
              Pre-Order Now - Ships by Christmas
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-bold text-mystical-gold mb-6 tracking-tight title-font">
              Mystical Oracle Card Deck
            </h1>
            
            <p className="text-xl md:text-2xl text-mystical-light mb-8 leading-relaxed body-font">
              Unlock the mysteries of the universe and connect with your inner wisdom. 
              Each card holds ancient secrets waiting to guide your spiritual journey.
            </p>
            
            <div className="flex justify-center items-center space-x-4 mb-8">
              <div className="flex items-center space-x-1 text-mystical-gold">
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
                <Star className="w-5 h-5 fill-current" />
              </div>
              <span className="text-mystical-light misc-font">Spiritual guidance at your fingertips</span>
            </div>
            
            <div className="text-4xl font-bold text-mystical-gold mb-2 title-font">
              $44.00
            </div>
            <p className="text-mystical-light mb-8 body-font">+ shipping calculated at checkout</p>
          </div>
        </div>
      </div>

      {/* Product Showcase */}
      <div className="py-16 bg-mystical-darker/30">
        <div className="container mx-auto px-4">
          {/* Progress Bar Section */}
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-mystical-gold mb-4 heading-font">
              Limited Pre-Order Release
            </h2>
            <div className="max-w-md mx-auto">
              <div className="flex justify-between text-sm text-mystical-light mb-2">
                <span className="body-font">Pre-orders secured</span>
                <span className="heading-font">{preorderStats.current_orders} / {preorderStats.max_orders}</span>
              </div>
              <div className="w-full bg-mystical-darker rounded-full h-3 border border-mystical-gold/30">
                <div 
                  className="bg-gradient-to-r from-mystical-gold to-yellow-400 h-full rounded-full transition-all duration-500 ease-out"
                  style={{width: `${preorderStats.percentage}%`}}
                ></div>
              </div>
              <p className="text-mystical-light/80 text-sm mt-2 body-font">
                🔥 Only {preorderStats.remaining} decks remaining in this initial release!
              </p>
            </div>
          </div>

          {/* Try Digital Version Button */}
          <div className="text-center mb-12">
            <a
              href="https://www.themagickmechanic.com/magick-cat-oracle-daniel-boutros"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span className="text-2xl">🔮</span>
              <div className="text-left">
                <div className="heading-font text-sm uppercase tracking-wider">Digital Magick Cat Oracle</div>
                <div className="body-font text-xs opacity-90">Experience the wisdom of the Magick Cat Oracle now, in digital form</div>
              </div>
            </a>
          </div>

          {/* Card Gallery */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-mystical-gold text-center mb-8 heading-font">
              Discover Your Mystical Journey
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-width-7xl mx-auto mb-8">
              <div className="group relative overflow-hidden rounded-lg shadow-lg card-hover">
                <img 
                  src="https://customer-assets.emergentagent.com/job_preorder-manager/artifacts/86vz5gcn_oracle-powerful.png"
                  alt="The Oracle Card"
                  className="w-full h-64 sm:h-72 object-cover object-top group-hover:scale-115 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-mystical-dark/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-2 left-2 text-mystical-gold font-semibold text-sm heading-font">
                    The Oracle ✨
                  </div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden rounded-lg shadow-lg card-hover">
                <img 
                  src="https://customer-assets.emergentagent.com/job_preorder-manager/artifacts/szzt8f56_Off%20ot%20Battle.png"
                  alt="Off to Battle Oracle Card"
                  className="w-full h-64 sm:h-72 object-cover object-top group-hover:scale-115 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-mystical-dark/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-2 left-2 text-mystical-gold font-semibold text-sm heading-font">
                    Off to Battle
                  </div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden rounded-lg shadow-lg card-hover">
                <img 
                  src="https://customer-assets.emergentagent.com/job_preorder-manager/artifacts/09byknrz_Dark%20Witch.png"
                  alt="Dark Witch Oracle Card"
                  className="w-full h-64 sm:h-72 object-cover object-top group-hover:scale-115 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-mystical-dark/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-2 left-2 text-mystical-gold font-semibold text-sm heading-font">
                    Dark Witch
                  </div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden rounded-lg shadow-lg card-hover">
                <img 
                  src="https://customer-assets.emergentagent.com/job_preorder-manager/artifacts/3tn857q6_magician2.png"
                  alt="Magician Oracle Card"
                  className="w-full h-64 sm:h-72 object-cover object-top group-hover:scale-115 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-mystical-dark/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-2 left-2 text-mystical-gold font-semibold text-sm heading-font">
                    The Magician
                  </div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden rounded-lg shadow-lg card-hover">
                <img 
                  src="https://customer-assets.emergentagent.com/job_preorder-manager/artifacts/dv8sgkrh_cup23.png"
                  alt="Cup Oracle Card"
                  className="w-full h-64 sm:h-72 object-cover object-top group-hover:scale-115 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-mystical-dark/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-2 left-2 text-mystical-gold font-semibold text-sm heading-font">
                    Sacred Cup
                  </div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden rounded-lg shadow-lg card-hover">
                <img 
                  src="https://customer-assets.emergentagent.com/job_preorder-manager/artifacts/rzwxn2yd_Tibetan%20Monk.png"
                  alt="Tibetan Monk Oracle Card"
                  className="w-full h-64 sm:h-72 object-cover object-top group-hover:scale-115 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-mystical-dark/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-2 left-2 text-mystical-gold font-semibold text-sm heading-font">
                    Tibetan Monk
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-center text-mystical-light text-lg body-font max-w-3xl mx-auto">
              Each card in your deck holds profound wisdom and mystical energy, 
              ready to guide you on your spiritual journey. Experience the full collection 
              of beautifully crafted oracle cards with premium quality printing and gold foil accents.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-mystical-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-10 h-10 text-mystical-gold" />
              </div>
              <h3 className="text-xl font-semibold text-mystical-gold mb-2 heading-font">Ancient Wisdom</h3>
              <p className="text-mystical-light body-font">Each card channels timeless spiritual insights and universal truths</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-mystical-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-10 h-10 text-mystical-gold" />
              </div>
              <h3 className="text-xl font-semibold text-mystical-gold mb-2 heading-font">Premium Quality</h3>
              <p className="text-mystical-light body-font">Luxurious card stock with mystical artwork and gold foil accents</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-mystical-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-mystical-gold" />
              </div>
              <h3 className="text-xl font-semibold text-mystical-gold mb-2 heading-font">Holiday Delivery</h3>
              <p className="text-mystical-light body-font">Guaranteed delivery by Christmas holidays for the perfect gift</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-Order Form Section - Clear Separator */}
      <div className="py-2 bg-mystical-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-mystical-gold mb-2 heading-font">
            🛒 Secure Your Oracle Deck Now
          </h2>
          <p className="text-mystical-light body-font">
            Complete the form below to pre-order your mystical oracle deck
          </p>
          <div className="mt-4 mb-2">
            <div className="inline-flex items-center text-mystical-gold font-semibold">
              <span className="mr-2">⬇️</span>
              <span className="heading-font">Scroll down to start your order</span>
              <span className="ml-2">⬇️</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-Order Form */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-mystical-card border-mystical-gold/20 shadow-2xl">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-mystical-gold">
                  {currentStep === 1 ? "Your Information" : 
                   currentStep === 2 ? "Shipping Address" : 
                   "Confirm Your Order"}
                </CardTitle>
                <CardDescription className="text-mystical-light">
                  Step {currentStep} of 3 - Secure your mystical oracle deck today
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                {message.text && (
                  <Alert className={`mb-6 ${
                    message.type === "success" ? "border-green-500 bg-green-500/10" :
                    message.type === "error" ? "border-red-500 bg-red-500/10" :
                    "border-mystical-gold bg-mystical-gold/10"
                  }`}>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className={
                      message.type === "success" ? "text-green-400" :
                      message.type === "error" ? "text-red-400" :
                      "text-mystical-gold"
                    }>
                      {message.text}
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {currentStep === 1 && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="email" className="text-mystical-light">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.customer.email}
                          onChange={(e) => handleInputChange("customer", "email", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="name" className="text-mystical-light">Full Name</Label>
                        <Input
                          id="name"
                          type="text"
                          value={formData.customer.name}
                          onChange={(e) => handleInputChange("customer", "name", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="phone" className="text-mystical-light">Phone Number (Optional)</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.customer.phone}
                          onChange={(e) => handleInputChange("customer", "phone", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                        />
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="shipping_name" className="text-mystical-light body-font">Recipient Name</Label>
                        <Input
                          id="shipping_name"
                          type="text"
                          value={formData.shipping_address.name}
                          onChange={(e) => handleInputChange("shipping_address", "name", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="address1" className="text-mystical-light body-font">Address Line 1</Label>
                        <Input
                          id="address1"
                          type="text"
                          value={formData.shipping_address.address_line_1}
                          onChange={(e) => handleInputChange("shipping_address", "address_line_1", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="address2" className="text-mystical-light body-font">Address Line 2 (Optional)</Label>
                        <Input
                          id="address2"
                          type="text"
                          value={formData.shipping_address.address_line_2}
                          onChange={(e) => handleInputChange("shipping_address", "address_line_2", e.target.value)}
                          className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="city" className="text-mystical-light body-font">City</Label>
                          <Input
                            id="city"
                            type="text"
                            value={formData.shipping_address.city}
                            onChange={(e) => handleInputChange("shipping_address", "city", e.target.value)}
                            className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                            required
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="country" className="text-mystical-light body-font">Country</Label>
                          <Select 
                            value={formData.shipping_address.country} 
                            onValueChange={(value) => handleInputChange("shipping_address", "country", value)}
                          >
                            <SelectTrigger className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold">
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent className="bg-mystical-darker border-mystical-gold/30">
                              <SelectItem value="US" className="text-mystical-light">United States</SelectItem>
                              <SelectItem value="CA" className="text-mystical-light">Canada</SelectItem>
                              <SelectItem value="GB" className="text-mystical-light">United Kingdom</SelectItem>
                              <SelectItem value="AU" className="text-mystical-light">Australia</SelectItem>
                              <SelectItem value="DE" className="text-mystical-light">Germany</SelectItem>
                              <SelectItem value="FR" className="text-mystical-light">France</SelectItem>
                              <SelectItem value="IT" className="text-mystical-light">Italy</SelectItem>
                              <SelectItem value="ES" className="text-mystical-light">Spain</SelectItem>
                              <SelectItem value="NL" className="text-mystical-light">Netherlands</SelectItem>
                              <SelectItem value="SE" className="text-mystical-light">Sweden</SelectItem>
                              <SelectItem value="NO" className="text-mystical-light">Norway</SelectItem>
                              <SelectItem value="DK" className="text-mystical-light">Denmark</SelectItem>
                              <SelectItem value="JP" className="text-mystical-light">Japan</SelectItem>
                              <SelectItem value="NZ" className="text-mystical-light">New Zealand</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="state_province" className="text-mystical-light body-font">
                            {formData.shipping_address.country === 'US' ? 'State' : 
                             formData.shipping_address.country === 'CA' ? 'Province' :
                             formData.shipping_address.country === 'GB' ? 'County' :
                             'State/Province'}
                          </Label>
                          <Input
                            id="state_province"
                            type="text"
                            value={formData.shipping_address.state_province}
                            onChange={(e) => handleInputChange("shipping_address", "state_province", e.target.value)}
                            className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                            placeholder={
                              formData.shipping_address.country === 'US' ? 'e.g., CA' :
                              formData.shipping_address.country === 'CA' ? 'e.g., ON' :
                              formData.shipping_address.country === 'GB' ? 'e.g., Surrey' :
                              'State/Province'
                            }
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="postal_code" className="text-mystical-light body-font">
                            {formData.shipping_address.country === 'US' ? 'ZIP Code' : 'Postal Code'}
                          </Label>
                          <Input
                            id="postal_code"
                            type="text"
                            value={formData.shipping_address.postal_code}
                            onChange={(e) => handleInputChange("shipping_address", "postal_code", e.target.value)}
                            className="bg-mystical-darker border-mystical-gold/30 text-mystical-light focus:border-mystical-gold"
                            placeholder={
                              formData.shipping_address.country === 'US' ? '12345' :
                              formData.shipping_address.country === 'GB' ? 'SW1A 1AA' :
                              formData.shipping_address.country === 'CA' ? 'K1A 0A6' :
                              'Postal Code'
                            }
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <div className="space-y-6">
                      <div className="bg-mystical-darker p-6 rounded-lg border border-mystical-gold/20">
                        <h3 className="text-lg font-semibold text-mystical-gold mb-4 heading-font">Order Summary</h3>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-mystical-light body-font">Mystical Oracle Card Deck</span>
                            <span className="text-mystical-gold body-font">$44.00</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-mystical-light body-font">Shipping</span>
                            <span className="text-mystical-gold body-font">Calculated at checkout</span>
                          </div>
                          <div className="border-t border-mystical-gold/20 pt-3">
                            <div className="flex justify-between text-lg font-semibold">
                              <span className="text-mystical-gold heading-font">Subtotal</span>
                              <span className="text-mystical-gold heading-font">$44.00</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-mystical-light mb-4 body-font">
                          Shipping to: {formData.customer.name}<br />
                          {formData.shipping_address.city}, {formData.shipping_address.country}
                        </p>
                        <p className="text-sm text-mystical-light/70 misc-font">
                          You'll be redirected to secure payment processing
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex justify-between pt-6">
                    {currentStep > 1 && (
                      <Button
                        type="button"
                        onClick={prevStep}
                        variant="outline"
                        className="border-mystical-gold/30 text-mystical-gold hover:bg-mystical-gold/10"
                      >
                        Previous
                      </Button>
                    )}
                    
                    {currentStep < 3 ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        disabled={!validateStep(currentStep)}
                        className="bg-mystical-gold text-mystical-dark hover:bg-mystical-gold/90 ml-auto"
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        disabled={isSubmitting || !validateStep(3)}
                        className="bg-mystical-gold text-mystical-dark hover:bg-mystical-gold/90 ml-auto"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          "Complete Pre-Order"
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;